import Box from '@mui/material/Box'
import React, { useCallback, useRef, useState } from 'react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { Reports } from './Reports'
import { Report } from './Report'
import resources from '../resources'
import { get, getBlob, post } from '../rest-utils'
import { useTranslation } from 'react-i18next'
import CenteredContent from '../CenteredContent'
import ReportActionMenu from './ReportActionMenu'

interface Props {
  initialReports: Reports
}

const reportsPageSize = Number(process.env.REPORTS_PAGE_SIZE ?? 10)

const IdentifiedReports = ({ initialReports }: Props) => {
  const { t } = useTranslation()
  const getLocalizedMovementType = (movementType: string) => {
    const movementTypeKey = `detectionReports.movementTypes.${movementType}`
    return t(movementTypeKey, { defaultValue: movementType })
  }
  const reportDivRef = useRef<HTMLDivElement>(null)
  const [reports, setReports] = useState<Reports>(initialReports)
  const [hasNextPage, setHasNextPage] = useState(initialReports?.reportsList?.length >= reportsPageSize)
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [loadingReportId, setLoadingReportId] = useState<bigint | null>(null)
  const [startDateInput, setStartDateInput] = useState('')
  const [endDateInput, setEndDateInput] = useState('')
  const [appliedStartDate, setAppliedStartDate] = useState('')
  const [appliedEndDate, setAppliedEndDate] = useState('')

  const [isDialogOpen, setIsDialogOpen] = React.useState(false)

  const handleClosePopup = () => {
    setIsDialogOpen(false)
  }

  const invoiceRequest = (report: Report, handleCloseActionMenu: () => void) => {
    handleCloseActionMenu()
  }

  const editReport = (report: Report, handleCloseActionMenu: () => void) => {
    setIsDialogOpen(true)
    handleCloseActionMenu()
  }

  function getReportJson(report: Report) {
    const registration = {
      registration: report.estimatedRegistration,
    }
    return registration
  }

  const toStartOfDayIso = (dateValue: string): string => {
    if (!dateValue) return ''
    return `${dateValue}T00:00:00`
  }

  const toEndOfDayIso = (dateValue: string): string => {
    if (!dateValue) return ''
    return `${dateValue}T23:59:59`
  }

  const validateReportModification = (report: Report) => {
    console.log('posting new report, report.estimatedRegistration : ', report.estimatedRegistration)
    post(`reports/${report.id}`, getReportJson(report)).catch((err) => console.log(err))
    handleClosePopup()
  }

  const archiveReport = (report: Report, handleCloseActionMenu: () => void) => {
    handleCloseActionMenu()
  }

  const downloadVideo = (report: Report, handleCloseActionMenu: () => void) => {
    handleCloseActionMenu()
    setLoadingReportId(report.id)
    getBlob(`videos?airportIcao=${report.airportIcao}&reportId=${report.id}`)
      .then((response) => {
        // Create blob link to download
        const url = URL.createObjectURL(response)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${report.airportIcao}_${report.reference}.mkv`)

        // Append to html link element page
        document.body.appendChild(link)

        // Start download
        link.click()

        // Clean up and remove the link
        if (link.parentNode) {
          link.parentNode.removeChild(link)
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoadingReportId(null))
  }

  const downloadImage = (report: Report, handleCloseActionMenu: () => void) => {
    handleCloseActionMenu()
    // Create blob link to download
    const url = report.registrationScreenshotURL
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${report.id}.jpeg`)

    // Append to html link element page
    document.body.appendChild(link)

    // Start download
    link.click()

    // Clean up and remove the link
    if (link.parentNode) {
      link.parentNode.removeChild(link)
    }
  }

  const fetchReportsPage = useCallback(
    async (
      pageNumber: number,
      options?: {
        preserveCurrentReportsOnEmpty?: boolean
        startDateParam?: string
        endDateParam?: string
      },
    ) => {
      setIsLoading(true)
      try {
        const startDateParam = options?.startDateParam ?? appliedStartDate
        const endDateParam = options?.endDateParam ?? appliedEndDate
        let apiURL = ''
        if (startDateParam && endDateParam) {
          apiURL = 'reports/by-dates?'
        } else {
          apiURL = 'reports/all?'
        }

        const queryParams = new URLSearchParams({
          airportIcao: initialReports.airportIcao,
          pageSize: String(reportsPageSize),
          pageNumber: String(pageNumber),
        })
        if (startDateParam && endDateParam) {
          queryParams.append('startDate', startDateParam)
          queryParams.append('endDate', endDateParam)
        }

        const airportsReportsResult = await get<Reports>(`${apiURL}${queryParams.toString()}`)
        const incomingReports = airportsReportsResult?.reportsList ?? []

        if (incomingReports.length === 0) {
          if (!options?.preserveCurrentReportsOnEmpty) {
            setReports({ airportIcao: initialReports.airportIcao, reportsList: [] })
            setCurrentPage(pageNumber)
          }
          setHasNextPage(false)
          return
        }

        const screenshots = await Promise.all(
          incomingReports.map((report) => getBlob(`screenshots?key=${report.registrationScreenshotKey}`)),
        )
        const hydratedReports = incomingReports.map((report, idx) => ({
          ...report,
          registrationScreenshotURL: URL.createObjectURL(screenshots[idx]),
        }))

        setReports(() => ({
          airportIcao: initialReports.airportIcao,
          reportsList: hydratedReports,
        }))
        setHasNextPage(hydratedReports.length >= reportsPageSize)
        setCurrentPage(pageNumber)
        reportDivRef.current?.scrollTo({ top: 0, behavior: 'auto' })
      } catch (err) {
        console.log(err)
      } finally {
        setIsLoading(false)
      }
    },
    [appliedEndDate, appliedStartDate, initialReports.airportIcao],
  )

  const handlePreviousPage = () => {
    if (isLoading || currentPage === 0) {
      return
    }
    void fetchReportsPage(currentPage - 1)
  }

  const handleNextPage = () => {
    if (isLoading || !hasNextPage) {
      return
    }
    void fetchReportsPage(currentPage + 1, { preserveCurrentReportsOnEmpty: true })
  }

  const handleApplyFilter = () => {
    const startIso = toStartOfDayIso(startDateInput)
    const endIso = toEndOfDayIso(endDateInput)

    // comparaison sur la date saisie (YYYY-MM-DD), fiable en string
    if (startDateInput && endDateInput && startDateInput > endDateInput) {
      return
    }

    setAppliedStartDate(startIso)
    setAppliedEndDate(endIso)
    setHasNextPage(true)
    setCurrentPage(0)
    void fetchReportsPage(0, { startDateParam: startIso, endDateParam: endIso })
  }

  const handleCancelFilter = () => {
    setStartDateInput('')
    setEndDateInput('')
    setAppliedStartDate('')
    setAppliedEndDate('')
    setHasNextPage(true)
    setCurrentPage(0)
    void fetchReportsPage(0, { startDateParam: '', endDateParam: '' })
  }

  const renderReport = (report: Report, key?: string) => {
    return (
      <Card
        sx={{ width: 830, minWidth: 700, marginRight: key ? 1 : 0, marginBottom: key ? 1 : 0 }}
        elevation={3}
        key={key}
      >
        <CardHeader
          title={
            <span style={{ whiteSpace: 'pre-line' }}>
              {t('detectionReports.reportIdentifiedTitle', {
                date: new Date(report.detectionDate).toLocaleString('fr-FR', {
                  timeZone: 'UTC',
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                }),
                registration: report.estimatedRegistration,
                movement: getLocalizedMovementType(report.movementType),
              })}
            </span>
          }
        />
        <CardContent sx={{ position: 'relative' }}>
          {loadingReportId === report.id && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255,255,255,0.7)',
                display: 'flex',
                flexDirection: 'column', // 👈 important
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10,
                gap: 2, // espace entre spinner et texte
              }}
            >
              <CircularProgress />

              <Typography variant="body1" color="text.secondary">
                {t('detectionReports.downloadOnGoingMessage')}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              maxWidth: '100%',
              paddingBottom: '15px',
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary" whiteSpace="pre-line" width={650}>
                {t('detectionReports.reportIdentifiedMessage', {
                  confidence: Math.round(report.registrationConfidence),
                })}
              </Typography>
              {report.pclAActivated && (
                <Typography variant="body2" color="text.secondary" whiteSpace="pre-line" paddingBottom={2} width={650}>
                  {t('detectionReports.reportMessageWithPCLA')}
                </Typography>
              )}
              {report.pclBActivated && (
                <Typography variant="body2" color="text.secondary" whiteSpace="pre-line" paddingBottom={2} width={650}>
                  {t('detectionReports.reportMessageWithPCLB')}
                </Typography>
              )}
            </Box>
            <ReportActionMenu
              invoiceCallBack={invoiceRequest}
              editCallBack={editReport}
              archiveCallBack={archiveReport}
              downloadVideoCallBack={downloadVideo}
              downloadImageCallBack={downloadImage}
              enableDownloadImage={true}
              report={report}
            />
            <Dialog
              open={isDialogOpen}
              onClose={handleClosePopup}
              PaperProps={{
                component: 'form',
                onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                  event.preventDefault()
                  const formData = new FormData(event.currentTarget)
                  // eslint-disable-next-line
                  const formJson = Object.fromEntries((formData as any).entries())
                  const registration = formJson.registration
                  report.estimatedRegistration = registration
                  validateReportModification(report)
                },
              }}
            >
              <DialogTitle>{t('detectionReports.editReport')}</DialogTitle>
              <DialogContent>
                <DialogContentText>{t('detectionReports.editReportMessage')}</DialogContentText>
                <TextField
                  autoFocus
                  required
                  margin="dense"
                  id="name"
                  name="registration"
                  type="text"
                  fullWidth
                  variant="standard"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClosePopup}>{t('detectionReports.cancelAction')}</Button>
                <Button type="submit">{t('detectionReports.editReport')}</Button>
              </DialogActions>
            </Dialog>
          </Box>

          {report.registrationScreenshotURL && (
            <img alt="" src={report.registrationScreenshotURL} height={400} width={800} />
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <Box
      ref={reportDivRef}
      sx={{
        height: 'calc(100vh - 114px)',
        overflowY: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 1, pt: 1.5, pb: 0.5 }}>
        <Box sx={{ width: 830, minWidth: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            type="date"
            label={t('detectionReports.filterStartDate')}
            value={startDateInput}
            onChange={(event) => setStartDateInput(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 160 }}
          />
          <TextField
            size="small"
            type="date"
            label={t('detectionReports.filterEndDate')}
            value={endDateInput}
            onChange={(event) => setEndDateInput(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 160 }}
          />
          <Button size="small" variant="contained" onClick={handleApplyFilter}>
            appliquer
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleCancelFilter}
            disabled={!appliedStartDate && !appliedEndDate && !startDateInput && !endDateInput}
          >
            annuler
          </Button>
        </Box>
      </Box>

      {reports?.reportsList?.length === 0 && !isLoading && (
        <Box sx={{ display: 'flex', marginY: 4, marginX: 'auto', typography: 'h5', justifyContent: 'center' }}>
          {t('detectionReports.noDetectionMessage')}
        </Box>
      )}

      {reports.reportsList.map((report: Report) => (
        <Box key={report.id} sx={{ padding: 1, display: 'flex', flex: '1 1 auto', justifyContent: 'center' }}>
          {renderReport(report)}
        </Box>
      ))}
      {(currentPage > 0 || hasNextPage) && reports.reportsList.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 1, pt: 1, pb: 2 }}>
          <Box sx={{ width: 830, minWidth: 700, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handlePreviousPage}
              disabled={isLoading || currentPage === 0}
            >
              {t('detectionReports.previousPage')}
            </Button>
            <Button size="small" variant="contained" onClick={handleNextPage} disabled={isLoading || !hasNextPage}>
              {t('detectionReports.nextPage')}
            </Button>
          </Box>
        </Box>
      )}
      {isLoading && (
        <CenteredContent>
          <CircularProgress />
        </CenteredContent>
      )}
    </Box>
  )
}

export default IdentifiedReports
